import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import { Container, TextField, Button, Typography, Box, InputAdornment, IconButton, Alert } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';
import config from '../config/config'; 

const LoginContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin-top: 128px; /* Double the height of the toolbar */
  background-color: white;
`;

const LoginBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const schema = yup.object().shape({
  username: yup.string().required('User Name is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});

function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  const [showPassword, setShowPassword] = useState(false);
  const [authMessage, setAuthMessage] = useState('');

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event) => event.preventDefault();

  const onSubmit = async (data) => {
    console.log('Form submitted:', data); // Debugging log
    try {
      const response = await axios.post(`${config.apiBaseUrl}/api/v1/auth/login`, {
        username: data.username,
        password: data.password,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'X-TenantID': 'admin',
        },
      });

      console.log('Response:', response); // Debugging log

      if (response.status === 200) {
        console.log('Authentication successful:', response.data);
        // Store the token (e.g., in localStorage)
        const token = response.data.token;
        localStorage.setItem('authToken', token);
        // Call the login function from AuthContext
        await login(data.username, token);
        setAuthMessage('Authentication successful');
        navigate('/dashboard');
      } else {
        console.error('Authentication failed:', response.data);
        setAuthMessage('Authentication failed');
      }
    } catch (error) {
      console.error('Error during authentication:', error);
      setAuthMessage('Authentication failed');
    }
  };

  return (
    <LoginContainer maxWidth="xs">
      <LoginBox>
        <Box mb={2} width="100%" display="flex" justifyContent="center">
          {/* Display the company logo */}
          <img src={logo} alt="Company Logo" width="75" height="75" />
        </Box>
        <Typography variant="h5" gutterBottom>
          Login to AMS
        </Typography>
        {authMessage && (
          <Alert severity={authMessage === 'Authentication successful' ? 'success' : 'error'}>
            {authMessage}
          </Alert>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mb={2}>
            <TextField
              label="User Name"
              variant="outlined"
              fullWidth
              {...register('username')}
              error={!!errors.username}
              helperText={errors.username?.message}
            />
          </Box>
          <Box mb={2}>
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              {...register('password')}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Button variant="contained" color="primary" fullWidth type="submit">
            Login
          </Button>
        </form>
      </LoginBox>
    </LoginContainer>
  );
}

export default Login;